import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import Chip from 'tio-reimagined/components/chip';
import PageHero from 'tio-reimagined/components/page-hero';
import {
  PhArrowLeft,
  PhArrowRight,
  PhChartDonut,
  PhCheckCircle,
  PhCode,
  PhCube,
  PhCursorClick,
  PhGridFour,
  PhLayout,
  PhListDashes,
  PhSidebar,
  PhTag,
  PhTextbox,
  PhTrendUp,
  PhWarning,
} from 'ember-phosphor-icons';

<template>
  {{! template-lint-disable no-bare-strings }}
  <VStack class="w-full min-w-0">
    {{! Back navigation }}
    <div class="mb-4">
      <LinkTo
        @route="authenticated.reimagined"
        class="inline-flex items-center gap-2 text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 font-medium"
      >
        <PhArrowLeft @size={{18}} />
        Back to Reimagined
      </LinkTo>
    </div>

    {{! Header }}
    <PageHero
      @title="Component Library"
      @subtitle="29 shared components in tio-reimagined. Battle-tested and ready for cross-app reuse."
      @icon={{PhCube}}
      @theme="gray"
      @decoration="dots"
    >
      <:actions>
        <Chip @intent="primary">
          <PhCode @size={{14}} class="mr-1" />
          29 Components
        </Chip>
        <Chip @intent="success">
          <PhCheckCircle @size={{14}} class="mr-1" />
          Fully Tested
        </Chip>
      </:actions>
    </PageHero>

    {{! ================================================================
          Deep-dive sub-pages — detailed demos & all API variants
          ================================================================ }}
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      <LinkTo
        @route="authenticated.reimagined.components.select"
        class="group relative overflow-hidden rounded-xl bg-linear-to-br from-violet-50 to-violet-100 dark:from-violet-950 dark:to-violet-900 p-5 ring-1 ring-violet-200 dark:ring-violet-800 hover:ring-violet-400 dark:hover:ring-violet-600 hover:shadow-lg transition-all duration-200"
      >
        <div class="flex items-start gap-3">
          <div
            class="w-10 h-10 rounded-lg bg-violet-500/10 dark:bg-violet-400/10 flex items-center justify-center shrink-0"
          >
            <PhListDashes @size={{20}} class="text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <span
              class="font-semibold text-gray-900 dark:text-white group-hover:text-violet-700 dark:group-hover:text-violet-300 transition-colors"
            >Select</span>
            <p class="text-xs text-gray-600 mt-0.5">Single, multi, search, remote</p>
          </div>
        </div>
        <PhArrowRight
          @size={{16}}
          class="absolute top-5 right-4 text-violet-400 opacity-0 group-hover:opacity-100 transition-opacity"
        />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.drawer"
        class="group relative overflow-hidden rounded-xl bg-linear-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 p-5 ring-1 ring-blue-200 dark:ring-blue-800 hover:ring-blue-400 dark:hover:ring-blue-600 hover:shadow-lg transition-all duration-200"
      >
        <div class="flex items-start gap-3">
          <div
            class="w-10 h-10 rounded-lg bg-blue-500/10 dark:bg-blue-400/10 flex items-center justify-center shrink-0"
          >
            <PhSidebar @size={{20}} class="text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <span
              class="font-semibold text-gray-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors"
            >Drawer</span>
            <p class="text-xs text-gray-600 mt-0.5">Placement, sizes, portaled overlay</p>
          </div>
        </div>
        <PhArrowRight
          @size={{16}}
          class="absolute top-5 right-4 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.cards"
        class="group relative overflow-hidden rounded-xl bg-linear-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950 dark:to-emerald-900 p-5 ring-1 ring-emerald-200 dark:ring-emerald-800 hover:ring-emerald-400 dark:hover:ring-emerald-600 hover:shadow-lg transition-all duration-200"
      >
        <div class="flex items-start gap-3">
          <div
            class="w-10 h-10 rounded-lg bg-emerald-500/10 dark:bg-emerald-400/10 flex items-center justify-center shrink-0"
          >
            <PhGridFour @size={{20}} class="text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <span
              class="font-semibold text-gray-900 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-300 transition-colors"
            >Cards</span>
            <p class="text-xs text-gray-600 mt-0.5">Actionable, Highlight, Glass, Step</p>
          </div>
        </div>
        <PhArrowRight
          @size={{16}}
          class="absolute top-5 right-4 text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.data-display"
        class="group relative overflow-hidden rounded-xl bg-linear-to-br from-amber-50 to-amber-100 dark:from-amber-950 dark:to-amber-900 p-5 ring-1 ring-amber-200 dark:ring-amber-800 hover:ring-amber-400 dark:hover:ring-amber-600 hover:shadow-lg transition-all duration-200"
      >
        <div class="flex items-start gap-3">
          <div
            class="w-10 h-10 rounded-lg bg-amber-500/10 dark:bg-amber-400/10 flex items-center justify-center shrink-0"
          >
            <PhTrendUp @size={{20}} class="text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <span
              class="font-semibold text-gray-900 dark:text-white group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors"
            >Data Display</span>
            <p class="text-xs text-gray-600 mt-0.5">StatCard, Progress, Pipeline, Timeline</p>
          </div>
        </div>
        <PhArrowRight
          @size={{16}}
          class="absolute top-5 right-4 text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.navigation"
        class="group relative overflow-hidden rounded-xl bg-linear-to-br from-cyan-50 to-cyan-100 dark:from-cyan-950 dark:to-cyan-900 p-5 ring-1 ring-cyan-200 dark:ring-cyan-800 hover:ring-cyan-400 dark:hover:ring-cyan-600 hover:shadow-lg transition-all duration-200"
      >
        <div class="flex items-start gap-3">
          <div
            class="w-10 h-10 rounded-lg bg-cyan-500/10 dark:bg-cyan-400/10 flex items-center justify-center shrink-0"
          >
            <PhLayout @size={{20}} class="text-cyan-600 dark:text-cyan-400" />
          </div>
          <div>
            <span
              class="font-semibold text-gray-900 dark:text-white group-hover:text-cyan-700 dark:group-hover:text-cyan-300 transition-colors"
            >Navigation</span>
            <p class="text-xs text-gray-600 mt-0.5">PageHero, NavCard, SectionHeader</p>
          </div>
        </div>
        <PhArrowRight
          @size={{16}}
          class="absolute top-5 right-4 text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.feedback"
        class="group relative overflow-hidden rounded-xl bg-linear-to-br from-rose-50 to-rose-100 dark:from-rose-950 dark:to-rose-900 p-5 ring-1 ring-rose-200 dark:ring-rose-800 hover:ring-rose-400 dark:hover:ring-rose-600 hover:shadow-lg transition-all duration-200"
      >
        <div class="flex items-start gap-3">
          <div
            class="w-10 h-10 rounded-lg bg-rose-500/10 dark:bg-rose-400/10 flex items-center justify-center shrink-0"
          >
            <PhWarning @size={{20}} class="text-rose-600 dark:text-rose-400" />
          </div>
          <div>
            <span
              class="font-semibold text-gray-900 dark:text-white group-hover:text-rose-700 dark:group-hover:text-rose-300 transition-colors"
            >Feedback</span>
            <p class="text-xs text-gray-600 mt-0.5">AlertBanner, EmptyState, IconBadge</p>
          </div>
        </div>
        <PhArrowRight
          @size={{16}}
          class="absolute top-5 right-4 text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.patterns"
        class="group relative overflow-hidden rounded-xl bg-linear-to-br from-indigo-50 to-indigo-100 dark:from-indigo-950 dark:to-indigo-900 p-5 ring-1 ring-indigo-200 dark:ring-indigo-800 hover:ring-indigo-400 dark:hover:ring-indigo-600 hover:shadow-lg transition-all duration-200"
      >
        <div class="flex items-start gap-3">
          <div
            class="w-10 h-10 rounded-lg bg-indigo-500/10 dark:bg-indigo-400/10 flex items-center justify-center shrink-0"
          >
            <PhTag @size={{20}} class="text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <span
              class="font-semibold text-gray-900 dark:text-white group-hover:text-indigo-700 dark:group-hover:text-indigo-300 transition-colors"
            >Patterns</span>
            <p class="text-xs text-gray-600 mt-0.5">FilterTabs, SkillChip, StatusBadge</p>
          </div>
        </div>
        <PhArrowRight
          @size={{16}}
          class="absolute top-5 right-4 text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.forms"
        class="group relative overflow-hidden rounded-xl bg-linear-to-br from-fuchsia-50 to-fuchsia-100 dark:from-fuchsia-950 dark:to-fuchsia-900 p-5 ring-1 ring-fuchsia-200 dark:ring-fuchsia-800 hover:ring-fuchsia-400 dark:hover:ring-fuchsia-600 hover:shadow-lg transition-all duration-200"
      >
        <div class="flex items-start gap-3">
          <div
            class="w-10 h-10 rounded-lg bg-fuchsia-500/10 dark:bg-fuchsia-400/10 flex items-center justify-center shrink-0"
          >
            <PhTextbox @size={{20}} class="text-fuchsia-600 dark:text-fuchsia-400" />
          </div>
          <div>
            <span
              class="font-semibold text-gray-900 dark:text-white group-hover:text-fuchsia-700 dark:group-hover:text-fuchsia-300 transition-colors"
            >Forms</span>
            <p class="text-xs text-gray-600 mt-0.5">Input, Textarea, Checkbox, Radio, Switch, Form</p>
          </div>
        </div>
        <PhArrowRight
          @size={{16}}
          class="absolute top-5 right-4 text-fuchsia-400 opacity-0 group-hover:opacity-100 transition-opacity"
        />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.buttons"
        class="group relative overflow-hidden rounded-xl bg-linear-to-br from-teal-50 to-teal-100 dark:from-teal-950 dark:to-teal-900 p-5 ring-1 ring-teal-200 dark:ring-teal-800 hover:ring-teal-400 dark:hover:ring-teal-600 hover:shadow-lg transition-all duration-200"
      >
        <div class="flex items-start gap-3">
          <div
            class="w-10 h-10 rounded-lg bg-teal-500/10 dark:bg-teal-400/10 flex items-center justify-center shrink-0"
          >
            <PhCursorClick @size={{20}} class="text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <span
              class="font-semibold text-gray-900 dark:text-white group-hover:text-teal-700 dark:group-hover:text-teal-300 transition-colors"
            >Button</span>
            <p class="text-xs text-gray-600 mt-0.5">Intents, appearances, sizes, loading, icons</p>
          </div>
        </div>
        <PhArrowRight
          @size={{16}}
          class="absolute top-5 right-4 text-teal-400 opacity-0 group-hover:opacity-100 transition-opacity"
        />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.chips"
        class="group relative overflow-hidden rounded-xl bg-linear-to-br from-sky-50 to-sky-100 dark:from-sky-950 dark:to-sky-900 p-5 ring-1 ring-sky-200 dark:ring-sky-800 hover:ring-sky-400 dark:hover:ring-sky-600 hover:shadow-lg transition-all duration-200"
      >
        <div class="flex items-start gap-3">
          <div
            class="w-10 h-10 rounded-lg bg-sky-500/10 dark:bg-sky-400/10 flex items-center justify-center shrink-0"
          >
            <PhTag @size={{20}} class="text-sky-600 dark:text-sky-400" />
          </div>
          <div>
            <span
              class="font-semibold text-gray-900 dark:text-white group-hover:text-sky-700 dark:group-hover:text-sky-300 transition-colors"
            >Chip</span>
            <p class="text-xs text-gray-600 mt-0.5">Intents, sizes, icons, status badges</p>
          </div>
        </div>
        <PhArrowRight
          @size={{16}}
          class="absolute top-5 right-4 text-sky-400 opacity-0 group-hover:opacity-100 transition-opacity"
        />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.charts"
        class="group relative overflow-hidden rounded-xl bg-linear-to-br from-orange-50 to-orange-100 dark:from-orange-950 dark:to-orange-900 p-5 ring-1 ring-orange-200 dark:ring-orange-800 hover:ring-orange-400 dark:hover:ring-orange-600 hover:shadow-lg transition-all duration-200"
      >
        <div class="flex items-start gap-3">
          <div
            class="w-10 h-10 rounded-lg bg-orange-500/10 dark:bg-orange-400/10 flex items-center justify-center shrink-0"
          >
            <PhChartDonut @size={{20}} class="text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <span
              class="font-semibold text-gray-900 dark:text-white group-hover:text-orange-700 dark:group-hover:text-orange-300 transition-colors"
            >Charts</span>
            <p class="text-xs text-gray-600 mt-0.5">Donut, Bar, Line, Pie, Area, Summary</p>
          </div>
        </div>
        <PhArrowRight
          @size={{16}}
          class="absolute top-5 right-4 text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity"
        />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>
    </div>

    {{! ================================================================
          Summary
          ================================================================ }}
    <div
      class="bg-violet-50 dark:bg-violet-950 rounded-xl p-6 border border-violet-100 dark:border-violet-800"
    >
      <div class="flex items-start gap-4">
        <div class="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
          <PhCode @size={{24}} class="text-violet-600" />
        </div>
        <div>
          <Heading @level={{3}} class="text-violet-900 dark:text-violet-100 mb-2">Component Library
            Summary</Heading>
          <Text @intent="muted" class="mb-4">
            29 shared components in tio-reimagined, fully tested and ready for cross-app reuse in
            tio-employee, tio-admin, and tio-agent.
          </Text>
          <Text @size="sm" @intent="muted" class="mb-2">tio-reimagined (29):</Text>
          <div class="flex flex-wrap gap-2 mb-3">
            <Chip @intent="success" @size="sm">ActionableCard</Chip>
            <Chip @intent="success" @size="sm">AlertBanner</Chip>
            <Chip @intent="primary" @size="sm">Button ✦</Chip>
            <Chip @intent="primary" @size="sm">Chip ✦</Chip>
            <Chip @intent="success" @size="sm">CircularProgress</Chip>
            <Chip @intent="success" @size="sm">Drawer</Chip>
            <Chip @intent="success" @size="sm">EmptyState</Chip>
            <Chip @intent="success" @size="sm">FaqAccordion</Chip>
            <Chip @intent="success" @size="sm">FilterTabs</Chip>
            <Chip @intent="success" @size="sm">GlassCard</Chip>
            <Chip @intent="success" @size="sm">HighlightCard</Chip>
            <Chip @intent="success" @size="sm">IconBadge</Chip>
            <Chip @intent="success" @size="sm">InfoRow</Chip>
            <Chip @intent="success" @size="sm">MatchScoreBadge</Chip>
            <Chip @intent="success" @size="sm">NavCard</Chip>
            <Chip @intent="success" @size="sm">PageHero</Chip>
            <Chip @intent="success" @size="sm">PipelineFlow</Chip>
            <Chip @intent="success" @size="sm">PipelineStage</Chip>
            <Chip @intent="success" @size="sm">ProgressBar</Chip>
            <Chip @intent="success" @size="sm">SectionHeader</Chip>
            <Chip @intent="success" @size="sm">SectionPanel</Chip>
            <Chip @intent="primary" @size="sm">Select ✦</Chip>
            <Chip @intent="success" @size="sm">SkillChip</Chip>
            <Chip @intent="success" @size="sm">StatCard</Chip>
            <Chip @intent="success" @size="sm">StatusBadge</Chip>
            <Chip @intent="success" @size="sm">StepCard</Chip>
            <Chip @intent="success" @size="sm">TaEligibilityBadge</Chip>
            <Chip @intent="success" @size="sm">Timeline</Chip>
            <Chip @intent="success" @size="sm">TimelineTracker</Chip>
          </div>
        </div>
      </div>
    </div>
  </VStack>
</template>
